// Every invoice, any time, as the Invoices tab under Reports since the Invoice Queue redesign:
// search, supplier, date and outcome filters, selection with Check again and PDF download, CSV export and paging.
// The work queue half of the old page (Queued, Ready to approve, the batch actions, Upload) lives in InvoiceQueue.
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiBlob, errorMessage, saveBlob } from "../api/client";
import type { Invoice } from "../api/types";
import { useInvoices, useSuppliers } from "../api/hooks";
import { useSession } from "../auth/SessionProvider";
import { CheckIcon, ErrorCard, LoadingRows, OutcomePill, ProgressCompact, mono, muted, table, td, th } from "../shared/components";
import { failedCheckOf, failedWording } from "../shared/checks";
import { OUTCOMES, actorName, fmtDay, fmtTime, isToday, isYesterday, money, outcomeOf, parseDate, poRef, stepIndex, type Outcome, stepWords } from "../shared/format";
import { Button, Card, Checkbox, Icon, Input, Select, useToast } from "../ui";
import { ChecksPassed, JobLink } from "../shared/invoiceParts";
import { ProblemDetail, ProblemDrawer, useInvoiceActions } from "./ProblemDetail";
import { FromEmailTag } from "./ReceivedEmails";

const PER = 25;

function startOf(when: string): string | undefined {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  if (when === "today") return d.toISOString();
  if (when === "yesterday") { d.setDate(d.getDate() - 1); return d.toISOString(); }
  if (when === "week") { d.setDate(d.getDate() - 6); return d.toISOString(); }
  return undefined;
}

function endOf(when: string): string | undefined {
  if (when !== "yesterday") return undefined;
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

/** The Outcome filter's choices: the resting outcome words, and what each asks the API for. */
const OUTCOME_FILTER: { value: string; label: string; status?: string; includeResolved?: boolean; adjusted?: boolean }[] = [
  { value: "all", label: "Any outcome", includeResolved: true },
  { value: "wait", label: OUTCOMES.wait.label, status: "Queued" },
  { value: "prog", label: OUTCOMES.prog.label, status: "Processing" },
  { value: "needs", label: OUTCOMES.rev.label, status: "NeedsReview,Failed" },
  { value: "ready", label: OUTCOMES.ready.label, status: "Held" },
  { value: "ok", label: OUTCOMES.ok.label, status: "Vouched" },
  // Any outcome where AutoVouch changed the PO by rule: freight set to the invoice's, or an Invoice adjustment line.
  { value: "adj", label: "Adjusted by AutoVouch", includeResolved: true, adjusted: true },
  { value: "res", label: OUTCOMES.res.label, status: "NeedsReview,Failed", includeResolved: true },
];

/** The second line under the outcome: who approved it, what stopped it, or the note a person left. */
function outcomeLine(i: Invoice, o: Outcome, readyLine: string): string {
  switch (o) {
    case "ok": {
      if (i.dryRun) return "preview – nothing changed in Syncore";
      const by = i.approvedBy;
      // Approved in Syncore without AutoVouch (a person there, or before AutoVouch got to it); the details page shows the invoice number it carries.
      if (by?.type === "syncore") return "in Syncore";
      // No batch (approved as soon as it matched) or the schedule: AutoVouch did it. A person or staff is named.
      if (!by || by.type === "system" || by.type === "scheduler") return "by AutoVouch";
      return "by " + actorName(by);
    }
    case "res": return i.resolved ? actorName(i.resolved.by) + ": “" + i.resolved.comment + "”" : "";
    case "rev": case "stop": case "set": return failedWording(failedCheckOf(i));
    case "ready": return readyLine;
    case "prog": return i.step ?? "";
    default: return "Not yet processed by AutoVouch";
  }
}

export function InvoicesTable() {
  const session = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const outcome = params.get("outcome") ?? "all";
  const q = params.get("q") ?? "";
  const when = params.get("when") ?? "all";
  const sup = params.get("supplier") ?? "";
  // The invoices one email produced (Reports › Received emails, "View the invoices in the Invoices tab").
  const ids = params.get("ids") ?? "";
  const page = Math.max(1, Number(params.get("page") ?? 1));
  const set = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) { if (v === null || v === "" || v === "all") next.delete(k); else next.set(k, v); }
    if (!("page" in patch)) next.delete("page");
    setParams(next, { replace: true });
  };
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  useEffect(() => {
    const t = window.setTimeout(() => { if (draft !== q) set({ q: draft }); }, 350);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const filter = OUTCOME_FILTER.find((f) => f.value === outcome) ?? OUTCOME_FILTER[0];
  const query = { status: filter.status, q: q || undefined, supplier: sup || undefined, from: startOf(when), to: endOf(when), ids: ids || undefined, page, pageSize: PER, includeResolved: filter.includeResolved || ids ? true : undefined, adjusted: filter.adjusted || undefined, sort: "receivedAt" };
  const invoices = useInvoices(query);
  const suppliers = useSuppliers();
  const actions = useInvoiceActions();
  const currency = actions.currency;
  const [sel, setSel] = useState<string[]>([]);
  const [drawer, setDrawer] = useState<string | null>(null);
  useEffect(() => setSel([]), [outcome, q, sup, when, ids, page]);

  const rows: Invoice[] = useMemo(() => {
    const items = invoices.data?.items ?? [];
    if (outcome === "ready") return items.filter((i) => i.awaitingRelease);
    if (outcome === "res") return items.filter((i) => !!i.resolved);
    if (outcome === "needs") return items.filter((i) => !i.resolved);
    return items;
  }, [invoices.data, outcome]);
  const total = invoices.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER));

  const selInv = rows.filter((i) => sel.includes(i.id));
  const fixable = selInv.filter((i) => (i.status === "NeedsReview" || i.status === "Failed") && !i.resolved);
  const allOn = rows.length > 0 && rows.every((i) => sel.includes(i.id));
  const toggle = (id: string) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const exportCsv = async () => {
    try {
      const { blob, fileName } = await apiBlob("/invoices/export", { query: { ...query, page: undefined, pageSize: undefined } });
      saveBlob(blob, fileName ?? "invoices.csv");
      toast({ tone: "success", title: "CSV exported" });
    } catch (e) { toast({ tone: "error", title: "Couldn’t export", message: errorMessage(e) }); }
  };
  const downloadZip = async () => {
    try {
      const { blob, fileName } = await apiBlob("/invoices/download", { method: "POST", body: { invoiceIds: sel } });
      saveBlob(blob, fileName ?? "invoices.zip");
      toast({ tone: "success", title: "Download started", message: sel.length + " PDFs" });
    } catch (e) { toast({ tone: "error", title: "Couldn’t download", message: errorMessage(e) }); }
  };

  const openInvoice = (i: Invoice) => {
    const o = outcomeOf(i);
    if (o === "rev" || o === "stop" || o === "set") { setDrawer(i.id); return; }
    if (o === "wait" || o === "prog") { toast({ tone: "info", title: o === "wait" ? "Queued" : "Still being compared", message: i.fileName }); return; }
    navigate(`/invoices/${i.id}`);
  };

  const now = new Date();
  // A day, with the time beneath it when it was today or yesterday; the same for Received and Approved.
  const stamp = (value: string) => {
    const d = parseDate(value);
    const recent = d && (isToday(d, now) || isYesterday(d, now));
    return <><div style={{ fontWeight: 600, color: "var(--text-strong)" }}>{fmtDay(value, now)}</div>{recent && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{fmtTime(value)}</div>}</>;
  };
  const received = (i: Invoice) => stamp(i.receivedAt);
  const checks = (i: Invoice, o: Outcome) => o === "prog" || o === "wait" ? <span style={{ color: "var(--text-subtle)" }}>—</span>
    : o === "rev" || o === "stop" || o === "set" || o === "res" ? <span style={{ display: "inline-flex", alignItems: "center", gap: 6, font: "600 13px var(--font-body)", color: "var(--status-mismatch-fg)" }}><CheckIcon r="no" size={16} />{failedWording(failedCheckOf(i))}</span>
    : <ChecksPassed freight={i.adjustments?.includes("freight")} lines={i.adjustments?.includes("lines")} />;

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Supplier, invoice # or PO" leadingIcon={<Icon name="search" size={16} />} containerStyle={{ width: 280 }} aria-label="Search" />
        <Select value={sup} onChange={(v) => set({ supplier: v })} options={[{ value: "", label: "All suppliers" }, ...(suppliers.data ?? []).map((s) => ({ value: s, label: s }))]} style={{ width: 220 }} />
        <Select value={when} onChange={(v) => set({ when: v })} options={[{ value: "all", label: "Any date" }, { value: "today", label: "Today" }, { value: "yesterday", label: "Yesterday" }, { value: "week", label: "Last 7 days" }]} style={{ width: 160 }} />
        <Select value={outcome} onChange={(v) => set({ outcome: v })} options={OUTCOME_FILTER.map((f) => ({ value: f.value, label: f.label }))} style={{ width: 190 }} />
        {ids && <FromEmailTag count={ids.split(",").filter(Boolean).length} onClear={() => set({ ids: null })} />}
        <span style={{ flex: 1 }} />
        <Button variant="outline" leadingIcon={<Icon name="download" size={16} />} onClick={exportCsv}>Export CSV</Button>
      </div>

      {sel.length > 0 && (
        <div style={{ position: "sticky", top: 12, zIndex: 20, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "10px 12px 10px 18px", marginBottom: 12, borderRadius: "var(--radius-lg)", background: "var(--surface-inverse)", color: "#fff", boxShadow: "var(--shadow-lg)", animation: "av-rise var(--dur-slow) var(--ease-out)" }}>
          <b style={{ font: "800 15px var(--font-display)" }}>{sel.length} selected</b><span style={{ flex: 1 }} />
          {session.canAct && <Button size="sm" disabled={!fixable.length} leadingIcon={<Icon name="refresh-cw" size={14} />} onClick={() => { actions.checkAgain(fixable.map((i) => i.id)); setSel([]); }}>Check again{fixable.length ? ` (${fixable.length})` : ""}</Button>}
          <Button size="sm" variant="outline" leadingIcon={<Icon name="file-archive" size={14} />} onClick={downloadZip}>Download PDFs</Button>
          <Button size="sm" variant="ghost" style={{ color: "#fff" }} onClick={() => setSel([])}>Clear</Button>
        </div>
      )}

      <Card padding="none">
        {invoices.isPending ? <LoadingRows rows={6} /> : invoices.error ? <div style={{ padding: 20 }}><ErrorCard error={invoices.error} retry={() => void invoices.refetch()} /></div> : rows.length === 0 ? (
          <div style={{ padding: "40px 24px", textAlign: "center", font: "var(--type-body)", color: "var(--text-muted)" }}>{ids ? "None of the invoices from that email is here any more." : q ? `No invoices match “${q}”.` : outcome === "adj" ? "AutoVouch hasn’t adjusted a PO for any of these." : outcome !== "all" ? `No invoices are ${filter.label.toLowerCase()}.` : "No invoices here yet. Forward one to your AutoVouch address or upload a PDF from the Invoice Queue."}</div>
        ) : (
          <>
            <table className="avp-tbl avp-top" style={table}>
              <thead><tr><th style={{ ...th, width: 44 }}><Checkbox checked={allOn} indeterminate={!allOn && rows.some((i) => sel.includes(i.id))} onChange={() => setSel(allOn ? sel.filter((id) => !rows.find((i) => i.id === id)) : Array.from(new Set([...sel, ...rows.map((i) => i.id)])))} /></th><th style={th}>Received</th><th style={th}>Approved</th><th style={th}>Supplier – invoice</th><th style={{ ...th, textAlign: "right" }}>Total</th><th style={th}>Outcome</th><th className="avp-md" style={th}>Checks</th><th style={{ ...th, width: 56 }}></th></tr></thead>
              <tbody>
                {rows.map((i) => {
                  const o = outcomeOf(i);
                  const click = o !== "prog" && o !== "wait";
                  const po = poRef(i);
                  return (
                    <tr key={i.id} className={click ? "avp-row" : ""} onClick={click ? () => openInvoice(i) : undefined} style={{ cursor: click ? "pointer" : "default" }}>
                      <td style={td} onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(i.id)} onChange={() => toggle(i.id)} /></td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{received(i)}</td>
                      <td style={{ ...td, whiteSpace: "nowrap" }}>{i.approvedAt ? stamp(i.approvedAt) : <span style={{ color: "var(--text-subtle)" }}>—</span>}</td>
                      <td style={td}><div style={{ font: "700 14px var(--font-body)", color: i.supplier ? "var(--text-strong)" : "var(--text-subtle)" }}>{i.supplier ?? (o === "wait" || o === "prog" ? "Reading…" : i.fileName)}</div><div style={{ ...mono, fontWeight: 500, fontSize: 12, color: "var(--text-muted)" }}>{i.invoiceNumber ?? i.fileName}{i.source === "upload" ? " – uploaded" : ""}</div>{po && <div style={{ ...mono, fontWeight: 500, fontSize: 12, color: "var(--text-muted)" }}>PO {po}</div>}</td>
                      <td style={{ ...td, ...mono, textAlign: "right", color: "var(--text-strong)" }}>{i.total == null ? <span style={{ fontWeight: 500, color: "var(--text-subtle)" }}>{o === "wait" || o === "prog" ? "Not read yet" : "—"}</span> : money(i.total, currency)}</td>
                      <td style={td}>
                        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 7 }}>
                          <OutcomePill o={o} size="sm" readyLine={actions.readyLine} />
                          {o === "prog" ? <ProgressCompact step={stepIndex(i.step)} label={stepWords(i.step)} fit /> : <span style={{ ...muted, fontSize: 12, lineHeight: 1.3 }}>{outcomeLine(i, o, actions.readyLine)}</span>}
                        </span>
                      </td>
                      <td className="avp-md" style={td}>{checks(i, o)}</td>
                      <td style={{ ...td, textAlign: "right" }} onClick={(e) => e.stopPropagation()}><JobLink jobNumber={i.jobNumber} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", borderTop: "1px solid var(--border-subtle)", ...muted }}>
              <span>{(page - 1) * PER + 1}–{Math.min(total, page * PER)} of {total}</span><span style={{ flex: 1 }} />
              <Button size="sm" variant="ghost" disabled={page <= 1} leadingIcon={<Icon name="chevron-left" size={14} />} onClick={() => set({ page: String(page - 1) })}>Previous</Button>
              <span style={{ fontWeight: 700, color: "var(--text-strong)" }}>{page} / {pages}</span>
              <Button size="sm" variant="ghost" disabled={page >= pages} trailingIcon={<Icon name="chevron-right" size={14} />} onClick={() => set({ page: String(page + 1) })}>Next</Button>
            </div>
          </>
        )}
      </Card>

      {drawer && <ProblemDrawer onClose={() => setDrawer(null)}><ProblemDetail id={drawer} ctx={null} mode="drawer" onClose={() => setDrawer(null)} actions={actions} /></ProblemDrawer>}
      {actions.dialogs}
    </>
  );
}
